import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { router } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import { Fonts } from '@/constants/theme';
import { fetchAssignmentsForTeacher, type AssignmentListItem } from '@/lib/assignments-admin';
import { fetchTeacherQuestionSets, type TeacherQuestionSetListItem } from '@/lib/question-sets';
import { useAuth } from '@/providers/auth-provider';

type QuickAction = {
  label: string;
  subtitle: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  route: '/teacher/sets' | '/teacher/scan' | '/teacher/results';
};

const quickActions: QuickAction[] = [
  {
    label: 'Create Set',
    subtitle: 'Build a fresh paper',
    icon: 'edit-note',
    route: '/teacher/sets',
  },
  {
    label: 'Scan OMR',
    subtitle: 'Check answer sheets',
    icon: 'qr-code-scanner',
    route: '/teacher/scan',
  },
  {
    label: 'Results',
    subtitle: 'Review scored attempts',
    icon: 'insights',
    route: '/teacher/results',
  },
];

function formatToday() {
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  }).format(new Date());
}

function buildInitials(name?: string | null) {
  if (!name?.trim()) return 'T';
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function getTransferredSetNote(
  set: Pick<TeacherQuestionSetListItem, 'teacher_id' | 'teacher_name'>,
  currentTeacherId?: string | null
) {
  if (!currentTeacherId || set.teacher_id === currentTeacherId) {
    return null;
  }

  const teacherName = set.teacher_name?.trim();
  return teacherName ? `Created by ${teacherName}` : 'Created by previous teacher';
}

export default function TeacherHomeScreen() {
  const { profile } = useAuth();
  const [assignments, setAssignments] = useState<AssignmentListItem[]>([]);
  const [questionSets, setQuestionSets] = useState<TeacherQuestionSetListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;

  const loadData = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    try {
      const [assignmentRows, setRows] = await Promise.all([
        fetchAssignmentsForTeacher(profile.id),
        fetchTeacherQuestionSets(profile.id),
      ]);
      setAssignments(assignmentRows);
      setQuestionSets(setRows);
    } catch (error) {
      console.error('Failed to load teacher dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const uniqueSubjects = useMemo(
    () => new Set(assignments.map((item) => item.subject_label)).size,
    [assignments]
  );
  const uniqueClasses = useMemo(
    () => new Set(assignments.map((item) => item.class_label)).size,
    [assignments]
  );
  const latestAssignment = assignments[0];
  const latestSet = questionSets[0];
  const recentSets = useMemo(
    () =>
      questionSets.slice(0, 3).map((set) => ({
        count: `${set.question_count} questions`,
        id: set.id,
        meta: `${set.class_label} - ${set.subject_label}`,
        mode: set.status,
        ownerNote: getTransferredSetNote(set, profile?.id),
        title: set.title,
      })),
    [profile?.id, questionSets]
  );
  const todayLabel = formatToday();
  const teacherName = profile?.full_name || 'Teacher';
  const latestSetOwnerNote = latestSet ? getTransferredSetNote(latestSet, profile?.id) : null;

  const stats = [
    { label: 'Classes', value: String(uniqueClasses || 0) },
    { label: 'Subjects', value: String(uniqueSubjects || 0) },
    { label: 'MCQ sets', value: String(questionSets.length) },
  ];

  if (isDesktop) {
    return (
      <View style={styles.desktopPage}>
        <ScrollView contentContainerStyle={styles.desktopScroll} showsVerticalScrollIndicator={false}>
          <View style={styles.desktopHero}>
            <View style={styles.desktopHeroTop}>
              <View style={styles.desktopHeroCopy}>
                <Text style={styles.desktopEyebrow}>Teacher home</Text>
                <Text style={styles.desktopTitle}>{teacherName}</Text>
                <Text style={styles.desktopSubtitle}>{profile?.school_name || 'School workspace'}</Text>
              </View>
            </View>

            <View style={styles.desktopHeroPanel}>
              <View style={styles.desktopHeroPanelMain}>
                <Text style={styles.desktopHeroDate}>{todayLabel}</Text>
                <Text style={styles.desktopHeroHeadline}>Dashboard</Text>
                <Text style={styles.desktopHeroBody}>
                  Monitor assignments, create sets, scan sheets, and review results from one screen.
                </Text>
              </View>

              <View style={styles.desktopStatsGrid}>
                {stats.map((item) => (
                  <View key={item.label} style={styles.desktopStatCard}>
                    <Text style={styles.desktopStatValue}>{item.value}</Text>
                    <Text style={styles.desktopStatLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.desktopGrid}>
            <View style={styles.desktopColumnLarge}>
              <View style={styles.desktopSection}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionTitle}>Quick actions</Text>
                  <Text style={styles.sectionCaption}>Open the next task directly.</Text>
                </View>
                <View style={styles.desktopActionGrid}>
                  {quickActions.map((action) => (
                    <Pressable
                      key={action.label}
                      onPress={() => router.push(action.route)}
                      style={styles.desktopActionCard}>
                      <View style={styles.desktopActionIcon}>
                        <MaterialIcons name={action.icon} size={22} color="#163828" />
                      </View>
                      <View style={styles.desktopActionCopy}>
                        <Text style={styles.desktopActionLabel}>{action.label}</Text>
                        <Text style={styles.desktopActionSubtitle}>{action.subtitle}</Text>
                      </View>
                      <MaterialIcons name="arrow-forward-ios" size={14} color="#6F7D71" />
                    </Pressable>
                  ))}
                </View>
              </View>

              <View style={styles.desktopSection}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionTitle}>Recent papers</Text>
                  <Pressable onPress={() => router.push('/teacher/sets')}>
                    <Text style={styles.linkText}>See all</Text>
                  </Pressable>
                </View>
                {loading ? (
                  <View style={styles.loadingWrap}>
                    <ActivityIndicator color="#163828" />
                  </View>
                ) : recentSets.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyTitle}>No MCQ sets yet</Text>
                    <Text style={styles.emptyText}>
                      Current assignment sets will appear here after they are created or transferred.
                    </Text>
                  </View>
                ) : (
                  <View style={styles.desktopRecentGrid}>
                    {recentSets.map((set, index) => (
                      <Pressable
                        key={set.id}
                        onPress={() => router.push('/teacher/sets')}
                        style={styles.desktopRecentCard}>
                        <View style={styles.desktopRecentIndex}>
                          <Text style={styles.desktopRecentIndexText}>{`0${index + 1}`}</Text>
                        </View>
                        <View style={styles.desktopRecentCopy}>
                          <Text style={styles.desktopRecentTitle}>{set.title}</Text>
                          <Text style={styles.desktopRecentMeta}>{set.meta}</Text>
                          {set.ownerNote ? (
                            <Text style={styles.desktopOwnerNoteText}>{set.ownerNote}</Text>
                          ) : null}
                        </View>
                        <View style={styles.desktopRecentRight}>
                          <Text style={styles.desktopRecentTag}>{set.mode}</Text>
                          <Text style={styles.desktopRecentCount}>{set.count}</Text>
                        </View>
                      </Pressable>
                    ))}
                  </View>
                )}
              </View>
            </View>

            <View style={styles.desktopColumnSide}>
              <View style={styles.desktopSection}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionTitle}>Teaching snapshot</Text>
                  <Text style={styles.sectionCaption}>Current assignment coverage.</Text>
                </View>
                <View style={styles.desktopSnapshotCard}>
                  <Text style={styles.snapshotLabel}>Latest MCQ set</Text>
                  <Text style={styles.desktopSnapshotTitle}>
                    {latestSet ? latestSet.title : latestAssignment ? latestAssignment.class_label : 'No data yet'}
                  </Text>
                  <Text style={styles.desktopSnapshotMeta}>
                    {latestSet
                      ? `${latestSet.class_label} - ${latestSet.subject_label}`
                      : latestAssignment
                      ? latestAssignment.subject_label
                      : 'Connect classes and subjects to start using the dashboard.'}
                  </Text>
                  {latestSetOwnerNote ? (
                    <Text style={styles.desktopOwnerNoteText}>{latestSetOwnerNote}</Text>
                  ) : null}
                  <Pressable onPress={() => router.push('/teacher/sets')} style={styles.snapshotButton}>
                    <Text style={styles.snapshotButtonText}>Open sets</Text>
                  </Pressable>
                </View>
              </View>

              <View style={styles.desktopSection}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionTitle}>Assignments</Text>
                  <Text style={styles.sectionCaption}>Mapped classes and subjects.</Text>
                </View>

                {loading ? (
                  <View style={styles.loadingWrap}>
                    <ActivityIndicator color="#163828" />
                  </View>
                ) : assignments.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyTitle}>No assignments yet</Text>
                    <Text style={styles.emptyText}>
                      Once classes are assigned, they will appear here as your working roster.
                    </Text>
                  </View>
                ) : (
                  <View style={styles.desktopAssignmentList}>
                    {assignments.map((item) => (
                      <View key={item.id} style={styles.desktopAssignmentRow}>
                        <View style={styles.assignmentLead}>
                          <View style={styles.assignmentDot} />
                          <View>
                            <Text style={styles.assignmentClass}>{item.class_label}</Text>
                            <Text style={styles.assignmentSubject}>{item.subject_label}</Text>
                          </View>
                        </View>
                        <MaterialIcons name="north-east" size={16} color="#6F7D71" />
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollable}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.heroShell}>
          <View style={styles.heroTopRow}>
            <Pressable onPress={() => router.push('/teacher/account')} style={styles.identityRow}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>{buildInitials(profile?.full_name)}</Text>
              </View>
              <View style={styles.identityText}>
                <Text style={styles.eyebrow}>Teacher Home</Text>
                <Text style={styles.name} numberOfLines={1}>
                  {teacherName}
                </Text>
              </View>
            </Pressable>
            <Pressable style={styles.headerIconBtn}>
              <MaterialIcons name="notifications-none" size={22} color="#F4EFE6" />
            </Pressable>
          </View>

          <View style={styles.heroPanel}>
            <View style={styles.heroBody}>
              <Text style={styles.heroKicker}>{todayLabel}</Text>
              <Text style={styles.heroTitle}>Prepare papers, scan sheets, and track classroom flow.</Text>
              <Text style={styles.heroSubtitle}>
                Keep generation, OMR review, and assignment coverage in one working surface.
              </Text>
            </View>

            <View style={styles.heroStatsRow}>
              {stats.map((item, index) => (
                <View
                  key={item.label}
                  style={[styles.heroStat, index > 0 ? styles.heroStatDivider : null]}
                >
                  <Text style={styles.heroStatValue}>{item.value}</Text>
                  <Text style={styles.heroStatLabel}>{item.label}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        <View style={styles.surface}>
          <View style={styles.sectionBlock}>
            <View style={styles.sectionHead}>
              <Text style={styles.sectionTitle}>Quick Actions</Text>
              <Text style={styles.sectionCaption}>Start the next task fast.</Text>
            </View>
            <View style={styles.actionRail}>
              {quickActions.map((action, index) => (
                <Pressable
                  key={action.label}
                  onPress={() => router.push(action.route)}
                  style={[
                    styles.actionItem,
                    index < quickActions.length - 1 ? styles.actionDivider : null,
                  ]}
                >
                  <View style={styles.actionIconWrap}>
                    <MaterialIcons name={action.icon} size={20} color="#163828" />
                  </View>
                  <View style={styles.actionCopy}>
                    <Text style={styles.actionLabel}>{action.label}</Text>
                    <Text style={styles.actionSubtitle}>{action.subtitle}</Text>
                  </View>
                  <MaterialIcons name="arrow-forward-ios" size={14} color="#6F7D71" />
                </Pressable>
              ))}
            </View>
          </View>

          <View style={styles.sectionBlock}>
            <View style={styles.sectionHead}>
              <Text style={styles.sectionTitle}>Teaching Snapshot</Text>
              <Text style={styles.sectionCaption}>What needs attention right now.</Text>
            </View>

            <View style={styles.snapshotPanel}>
              <View style={styles.snapshotMain}>
                <Text style={styles.snapshotLabel}>Latest MCQ set</Text>
                <Text style={styles.snapshotValue}>
                  {latestSet ? latestSet.title : latestAssignment ? latestAssignment.class_label : 'No data yet'}
                </Text>
                <Text style={styles.snapshotMeta}>
                  {latestSet
                    ? `${latestSet.class_label} - ${latestSet.subject_label}`
                    : latestAssignment
                    ? latestAssignment.subject_label
                    : 'Connect classes and subjects to start using the dashboard.'}
                </Text>
                {latestSetOwnerNote ? (
                  <Text style={styles.ownerNoteText}>{latestSetOwnerNote}</Text>
                ) : null}
              </View>
              <Pressable onPress={() => router.push('/teacher/sets')} style={styles.snapshotButton}>
                <Text style={styles.snapshotButtonText}>Open sets</Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.sectionBlock}>
            <View style={styles.sectionHead}>
              <Text style={styles.sectionTitle}>Recent Papers</Text>
              <Pressable onPress={() => router.push('/teacher/sets')}>
                <Text style={styles.linkText}>See all</Text>
              </Pressable>
            </View>
            {loading ? (
              <View style={styles.loadingWrap}>
                <ActivityIndicator color="#163828" />
              </View>
            ) : recentSets.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyTitle}>No MCQ sets yet</Text>
                <Text style={styles.emptyText}>
                  Current assignment sets will appear here after they are created or transferred.
                </Text>
              </View>
            ) : (
              <View style={styles.listBlock}>
                {recentSets.map((set, index) => (
                  <Pressable key={set.id} onPress={() => router.push('/teacher/sets')} style={styles.listRow}>
                    <View style={styles.rowIndex}>
                      <Text style={styles.rowIndexText}>{`0${index + 1}`}</Text>
                    </View>
                    <View style={styles.rowCopy}>
                      <Text style={styles.rowTitle}>{set.title}</Text>
                      <Text style={styles.rowMeta}>{set.meta}</Text>
                      {set.ownerNote ? <Text style={styles.ownerNoteText}>{set.ownerNote}</Text> : null}
                    </View>
                    <View style={styles.rowRight}>
                      <Text style={styles.rowTag}>{set.mode}</Text>
                      <Text style={styles.rowCount}>{set.count}</Text>
                    </View>
                  </Pressable>
                ))}
              </View>
            )}
          </View>

          <View style={styles.sectionBlock}>
            <View style={styles.sectionHead}>
              <Text style={styles.sectionTitle}>Assignments</Text>
              <Text style={styles.sectionCaption}>Mapped classes and subjects.</Text>
            </View>

            {loading ? (
              <View style={styles.loadingWrap}>
                <ActivityIndicator color="#163828" />
              </View>
            ) : assignments.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyTitle}>No assignments yet</Text>
                <Text style={styles.emptyText}>
                  Once classes are assigned, they will appear here as your working roster.
                </Text>
              </View>
            ) : (
              <View style={styles.assignmentList}>
                {assignments.map((item) => (
                  <View key={item.id} style={styles.assignmentRow}>
                    <View style={styles.assignmentLead}>
                      <View style={styles.assignmentDot} />
                      <View>
                        <Text style={styles.assignmentClass}>{item.class_label}</Text>
                        <Text style={styles.assignmentSubject}>{item.subject_label}</Text>
                      </View>
                    </View>
                    <MaterialIcons name="north-east" size={16} color="#6F7D71" />
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  desktopPage: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  desktopScroll: {
    padding: 28,
    gap: 24,
  },
  desktopHero: {
    gap: 18,
  },
  desktopHeroTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
  },
  desktopHeroCopy: {
    gap: 6,
    flex: 1,
  },
  desktopEyebrow: {
    color: '#6F7D71',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '800',
  },
  desktopTitle: {
    color: '#163828',
    fontSize: 34,
    lineHeight: 40,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopSubtitle: {
    color: '#6F7D71',
    fontSize: 14,
    lineHeight: 20,
  },
  desktopHeroPanel: {
    backgroundColor: '#103427',
    borderRadius: 28,
    padding: 22,
    gap: 18,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.09)',
  },
  desktopHeroPanelMain: {
    gap: 8,
    maxWidth: 560,
  },
  desktopHeroDate: {
    color: '#BBD0C2',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  desktopHeroHeadline: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopHeroBody: {
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 21,
    maxWidth: 620,
  },
  desktopStatsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  desktopStatCard: {
    flex: 1,
    borderRadius: 18,
    backgroundColor: 'rgba(244, 239, 230, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.1)',
    padding: 16,
  },
  desktopStatValue: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 32,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopStatLabel: {
    color: '#9EB5A7',
    fontSize: 12,
    marginTop: 4,
    fontWeight: '700',
  },
  desktopGrid: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },
  desktopColumnLarge: {
    flex: 1.4,
    gap: 20,
    minWidth: 0,
  },
  desktopColumnSide: {
    flex: 0.9,
    gap: 20,
    minWidth: 0,
  },
  desktopSection: {
    gap: 14,
  },
  desktopActionGrid: {
    gap: 10,
  },
  desktopActionCard: {
    backgroundColor: '#F6F1E8',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#E7DED1',
    paddingHorizontal: 16,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  desktopActionIcon: {
    width: 46,
    height: 46,
    borderRadius: 16,
    backgroundColor: '#E1EADD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopActionCopy: {
    flex: 1,
    gap: 2,
  },
  desktopActionLabel: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
  },
  desktopActionSubtitle: {
    color: '#6F7D71',
    fontSize: 12,
  },
  desktopRecentGrid: {
    gap: 10,
  },
  desktopRecentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 16,
    borderRadius: 20,
    backgroundColor: '#FBF8F2',
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  desktopRecentIndex: {
    width: 38,
    alignItems: 'center',
  },
  desktopRecentIndexText: {
    color: '#7E8F82',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
  },
  desktopRecentCopy: {
    flex: 1,
    gap: 3,
  },
  desktopRecentTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopRecentMeta: {
    color: '#6F7D71',
    fontSize: 12,
  },
  desktopOwnerNoteText: {
    color: '#849286',
    fontSize: 11,
    lineHeight: 15,
  },
  desktopRecentRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  desktopRecentTag: {
    color: '#163828',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: '800',
  },
  desktopRecentCount: {
    color: '#6F7D71',
    fontSize: 12,
  },
  desktopSnapshotCard: {
    backgroundColor: '#163828',
    borderRadius: 24,
    padding: 18,
    gap: 8,
  },
  desktopSnapshotTitle: {
    color: '#F7F4EE',
    fontSize: 22,
    lineHeight: 28,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopSnapshotMeta: {
    color: '#C7D5CB',
    fontSize: 13,
    lineHeight: 19,
  },
  desktopAssignmentList: {
    gap: 10,
  },
  desktopAssignmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: 18,
    backgroundColor: '#FBF8F2',
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  container: {
    flex: 1,
    backgroundColor: '#103427',
  },
  scrollable: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  heroShell: {
    paddingTop: 60,
    paddingHorizontal: 20,
    paddingBottom: 28,
    backgroundColor: '#103427',
  },
  heroTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 18,
  },
  identityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    flexShrink: 1,
    flex: 1,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#F4EFE6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#103427',
    fontSize: 16,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  identityText: {
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    color: '#9EB5A7',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '700',
  },
  name: {
    color: '#F7F4EE',
    fontSize: 20,
    fontFamily: Fonts.rounded,
    fontWeight: '700',
  },
  headerIconBtn: {
    width: 42,
    height: 42,
    borderRadius: 21,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.16)',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(244, 239, 230, 0.06)',
    marginLeft: 14,
  },
  heroPanel: {
    borderRadius: 26,
    paddingHorizontal: 18,
    paddingTop: 18,
    paddingBottom: 6,
    backgroundColor: 'rgba(244, 239, 230, 0.06)',
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.1)',
  },
  heroBody: {
    gap: 10,
    marginBottom: 24,
    maxWidth: 360,
    width: '100%',
  },
  heroKicker: {
    color: '#C7D5CB',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
  heroTitle: {
    color: '#F7F4EE',
    fontSize: 30,
    lineHeight: 36,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  heroSubtitle: {
    color: '#BCD0C3',
    fontSize: 14,
    lineHeight: 21,
  },
  heroStatsRow: {
    flexDirection: 'row',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(244, 239, 230, 0.12)',
  },
  heroStat: {
    flex: 1,
    paddingTop: 4,
    paddingBottom: 14,
  },
  heroStatDivider: {
    paddingLeft: 14,
    marginLeft: 14,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(244, 239, 230, 0.12)',
  },
  heroStatValue: {
    color: '#F7F4EE',
    fontSize: 24,
    lineHeight: 28,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  heroStatLabel: {
    color: '#9EB5A7',
    fontSize: 12,
    marginTop: 4,
  },
  surface: {
    backgroundColor: '#F4EFE6',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    paddingHorizontal: 20,
    paddingTop: 24,
    gap: 28,
  },
  sectionBlock: {
    gap: 16,
  },
  sectionHead: {
    gap: 4,
  },
  sectionTitle: {
    color: '#163828',
    fontSize: 20,
    lineHeight: 24,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  sectionCaption: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  actionRail: {
    backgroundColor: '#EFE7DA',
    borderRadius: 24,
    overflow: 'hidden',
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  actionDivider: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(22, 56, 40, 0.08)',
  },
  actionIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#D9E1D8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionCopy: {
    flex: 1,
    gap: 2,
  },
  actionLabel: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '700',
  },
  actionSubtitle: {
    color: '#6F7D71',
    fontSize: 12,
  },
  snapshotPanel: {
    backgroundColor: '#163828',
    borderRadius: 24,
    padding: 18,
    gap: 18,
  },
  snapshotMain: {
    gap: 6,
  },
  snapshotLabel: {
    color: '#A4B9AB',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: '700',
  },
  snapshotValue: {
    color: '#F7F4EE',
    fontSize: 24,
    lineHeight: 30,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  snapshotMeta: {
    color: '#C7D5CB',
    fontSize: 14,
    lineHeight: 20,
  },
  ownerNoteText: {
    color: '#AFC2B5',
    fontSize: 11,
    lineHeight: 16,
  },
  snapshotButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#F4EFE6',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
  },
  snapshotButtonText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '700',
  },
  linkText: {
    color: '#47624F',
    fontSize: 13,
    fontWeight: '700',
  },
  listBlock: {
    gap: 10,
  },
  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(22, 56, 40, 0.08)',
  },
  rowIndex: {
    width: 38,
    alignItems: 'center',
  },
  rowIndexText: {
    color: '#7E8F82',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.1,
  },
  rowCopy: {
    flex: 1,
    gap: 3,
  },
  rowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '700',
  },
  rowMeta: {
    color: '#6F7D71',
    fontSize: 12,
  },
  rowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  rowTag: {
    color: '#163828',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: '800',
  },
  rowCount: {
    color: '#6F7D71',
    fontSize: 12,
  },
  loadingWrap: {
    paddingVertical: 24,
  },
  emptyState: {
    backgroundColor: '#EFE7DA',
    borderRadius: 24,
    padding: 18,
    gap: 8,
  },
  emptyTitle: {
    color: '#163828',
    fontSize: 16,
    fontWeight: '700',
  },
  emptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  assignmentList: {
    gap: 10,
  },
  assignmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(22, 56, 40, 0.08)',
  },
  assignmentLead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 1,
  },
  assignmentDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#163828',
  },
  assignmentClass: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '700',
  },
  assignmentSubject: {
    color: '#6F7D71',
    fontSize: 12,
    marginTop: 2,
  },
});
